import { useState } from 'react';

import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useClassifierConfigList } from '@/hooks/useClassifierConfigs';
import { useDeleteJudgeTuningRun, useJudgeTuningRunList } from '@/hooks/useJudgeTuning';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useQualityConfigList } from '@/hooks/useQualityConfigs';

import type { JudgeTuningStatus } from '@/lib/api/judge-tuning.api';

const STATUS_VARIANT: Record<
  JudgeTuningStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  DRAFT: 'outline',
  QUEUED: 'secondary',
  RUNNING: 'secondary',
  SUCCEEDED: 'default',
  PARTIAL: 'secondary',
  FAILED: 'destructive',
  CANCELLED: 'outline',
};

const pct = (rate: number | undefined) => (rate === undefined ? '—' : `${Math.round(rate * 100)}%`);

export const JudgeTuningListPage = () => {
  usePageTitle('Judge Tuning');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: runs, isLoading } = useJudgeTuningRunList();
  const { data: classifierConfigs } = useClassifierConfigList();
  const { data: qualityConfigs } = useQualityConfigList();
  const deleteMutation = useDeleteJudgeTuningRun();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const configName = (targetType: string, id: string) =>
    (targetType === 'quality' ? qualityConfigs : classifierConfigs)?.find((c) => c.id === id)
      ?.name ?? id.slice(0, 8);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast({
        variant: 'success',
        title: 'Deleted',
        description: `'${deleteTarget.name}' removed.`,
      });
      setDeleteTarget(null);
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete run.' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Judge Tuning</CardTitle>
            <CardDescription>
              Improve a classifier&apos;s prompt from mis-classified sample calls. The optimizer
              rewrites the prompt and re-tests it against the samples; optionally auto-applies the
              result to the target config.
            </CardDescription>
          </div>
          <Button onClick={() => navigate('/agent-improvement/judge-tuning/new')}>
            <Plus className="mr-2 h-4 w-4" /> New run
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Target configs</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pass rate</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Auto-update</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(runs ?? []).map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell className="text-xs">
                    {l.target_all_used_configs ? (
                      <span className="text-muted-foreground italic">all used configs</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {l.target_config_ids.map((id) => (
                          <Badge key={id} variant="outline" className="text-xs font-normal">
                            {configName(l.target_type, id)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[l.status]}>{l.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {l.result_summary?.baselinePassRate !== undefined ? (
                      <span>
                        {pct(l.result_summary.baselinePassRate)} →{' '}
                        {pct(l.result_summary.bestPassRate)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {l.sample_tags.length ? (
                      <div className="flex flex-wrap gap-1">
                        {l.sample_tags.map((t) => (
                          <Badge key={t} variant="secondary" className="text-xs">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">all</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {l.auto_update ? (
                      <Badge variant="secondary">on</Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">off</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(l.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/agent-improvement/judge-tuning/${l.id}`)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget({ id: l.id, name: l.name })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        resourceName="judge tuning run"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
